import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { noWhitespaceValidator } from 'src/app/common-components/validators/no-whitespace.validator';
import { LocationDetailForm, LocationDetailFormValue } from '../models/location-detail-form';
import { LocationDetailSaveModel } from '../models/location-detail-save.model';
import { LocationDetailView } from '../models/location-detail-view.model';

@Injectable()
export class LocationDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   pattern = '^\\s*([A-Za-z0-9]+(?: [A-Za-z0-9]+)*)\\s*$';

   constructor(fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();
      const form: LocationDetailForm = {
         account: fb.control(null),
         name: fb.control(null, [Validators.maxLength(255), containsCharactersValidator()]),
         city: fb.control(null, Validators.maxLength(255)),
         comment: fb.control(null, Validators.maxLength(50)),
         country: fb.control(null),
         createDate: fb.control(null),
         createUser: fb.control(null),
         directionalPostfix: fb.control(null, Validators.maxLength(255)),
         directionalPrefix: fb.control(null, Validators.maxLength(255)),
         houseNumber: fb.control(null, [Validators.maxLength(255)]),
         isE911Validated: fb.control(null),
         locationId: fb.control(null),
         mostRecentE911ValidationResponse: fb.control(null),
         postalCode: fb.control(null, [noWhitespaceValidator()]),
         state: fb.control(null, [Validators.required, Validators.pattern('^[A-Z]{2}$'), noWhitespaceValidator()]),
         streetName: fb.control(null, Validators.maxLength(255)),
         streetNameSuffix: fb.control(null, Validators.maxLength(255)),
         timeZone: fb.control(null),
         updateDate: fb.control(null),
         updateUser: fb.control(null),
      };
      this.form = fb.group(form);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updateLocation(location: LocationDetailView): void {
      if (!location) {
         return;
      }

      const formValue: Partial<LocationDetailFormValue> = {
         account: location.account,
         name: location.name,
         city: location.city,
         comment: location.comment,
         country: location.country,
         directionalPostfix: location.directionalPostfix,
         directionalPrefix: location.directionalPrefix,
         houseNumber: location.houseNumber,
         isE911Validated: location.isE911Validated,
         locationId: location.locationId,
         mostRecentE911ValidationResponse: location.mostRecentE911ValidationResponse,
         postalCode: location.postalCode,
         state: location.state,
         streetName: location.streetName,
         streetNameSuffix: location.streetNameSuffix,
         timeZone: { timeZoneId: location?.timeZoneId ?? null },
      };

      this.form.patchValue(formValue);
      this.markAsUntouchedAndPristine();
   }

   getValue(locationId: number | null): LocationDetailSaveModel {
      const value = this.form.value as LocationDetailFormValue;
      return {
         accountId: value.account?.accountId ?? null,
         name: value.name,
         city: value.city,
         country: value.country,
         comment: value.comment,
         directionalPostfix: value.directionalPostfix,
         directionalPrefix: value.directionalPrefix,
         houseNumber: value.houseNumber,
         locationId: locationId,
         postalCode: value.postalCode,
         state: value.state,
         streetName: value.streetName,
         streetNameSuffix: value.streetNameSuffix,
         timeZoneId: value.timeZone?.timeZoneId ?? null,
      };
   }
}
